/**
 * `@crown/ui/i18n`: the shared package's own catalogue and the browser
 * runtime of `@crown/i18n`, so an app configures both in one call:
 *
 *   configureI18n({
 *     catalogues: mergeCatalogueSets(uiCatalogues, appCatalogues),
 *     context: resolveBrowserContext({ user, organization, workspace }, uiCatalogues),
 *   });
 *
 * Components read their text through `translate` and mark it with `data-i18n`, so
 * they render English before any app configures a locale and re-render when
 * one does.
 */
import {
  availableLocales,
  type CatalogueSet,
  type I18nContextInput,
  type MessageArgs,
  mergeCatalogueSets,
} from '@crown/i18n';
import { hasGlobal, isObjectLike, isString } from '@crown/framework/ir';
import coreEnGB from '@crown/i18n/locales/en-GB.json';
import coreClinical from '@crown/i18n/locales/en-GB.clinical.json';
import coreEducation from '@crown/i18n/locales/en-GB.education.json';
import coreSurvey from '@crown/i18n/locales/en-GB.survey.json';
import coreEnUS from '@crown/i18n/locales/en-US.json';
import coreCy from '@crown/i18n/locales/cy.json';
import coreCyClinical from '@crown/i18n/locales/cy.clinical.json';
import coreCyEducation from '@crown/i18n/locales/cy.education.json';
import coreCySurvey from '@crown/i18n/locales/cy.survey.json';
import {
  addCatalogues,
  applyDocumentLocale,
  bindI18n,
  configureI18n,
  locale,
  localeDisplayName,
  registeredCatalogues,
  rememberLocale,
  resolveBrowserContext,
  setLocale,
  setUseCase,
  translate,
} from '@crown/i18n/ui';
import { effect } from '@crown/signal/effect';
import cy from '../locales/cy.json';
import enGB from '../locales/en-GB.json';

/** The settings an app knows about the caller: the same shape the server middleware takes. */
export type I18nSettings = Omit<I18nContextInput, 'requested' | 'available'>;

export type UiMessageKey = keyof typeof enGB | keyof typeof coreEnGB;

/** `common.*`, `validation.*` and `term.*` from `@crown/i18n` plus this package's `ui.*`. */
export const uiCatalogues: CatalogueSet = mergeCatalogueSets(
  {
    'en-GB': coreEnGB,
    'en-GB.clinical': coreClinical,
    'en-GB.survey': coreSurvey,
    'en-GB.education': coreEducation,
    'en-US': coreEnUS,
    cy: coreCy,
    'cy.clinical': coreCyClinical,
    'cy.survey': coreCySurvey,
    'cy.education': coreCyEducation,
    // Offered from the start, their words fetched when first chosen (below).
    gd: {},
    ar: {},
  },
  { 'en-GB': enGB, cy },
);

addCatalogues(uiCatalogues);

/**
 * Languages whose words are fetched the first time a reader chooses one.
 * Gaelic and Arabic cost about fourteen kilobytes compressed together, which
 * every page read in English or Welsh - a public form's first load most of
 * all - would otherwise download for nothing.
 */
const LAZY_LOCALES = new Map([
  ['gd', () => import(/* webpackChunkName: "locale-gd" */ './i18n-locales/gd')],
  ['ar', () => import(/* webpackChunkName: "locale-ar" */ './i18n-locales/ar')],
]);
const lazyCatalogues = new Map<string, Promise<CatalogueSet>>();

/**
 * Registers a lazily loaded language's words, fetching them the first time.
 * Resolves at once for a language that ships with the page. The words are
 * registered again if an app has since replaced the catalogues.
 */
export async function ensureLocaleCatalogues(tag: string): Promise<void> {
  const language = tag.split('-')[0] ?? tag;
  const load = LAZY_LOCALES.get(language);
  if (!load) return;
  let pending = lazyCatalogues.get(language);
  if (!pending) {
    pending = load().then((module) => module.catalogues);
    lazyCatalogues.set(language, pending);
  }
  const catalogues = await pending.catch((error: Error) => {
    // The page stays in English rather than failing; a later choice retries.
    lazyCatalogues.delete(language);
    console.warn(`[i18n] the ${language} catalogue could not be loaded`, error);
    return undefined;
  });
  if (!catalogues) return;
  const probe = Object.keys(catalogues[language] ?? {})[0];
  if (
    probe !== undefined &&
    registeredCatalogues()[language]?.[probe] === undefined
  )
    addCatalogues(catalogues);
}

// A locale reached any other way - remembered, the account's, the
// workspace's - fetches its words too, and again after `configureI18n`
// replaces the set.
effect(() => {
  const current = locale.get();
  if (!LAZY_LOCALES.has(current.split('-')[0] ?? current)) return;
  registeredCatalogues();
  void ensureLocaleCatalogues(current);
});

export {
  addCatalogues,
  applyDocumentLocale,
  bindI18n,
  configureI18n,
  i18nSnapshot,
  type I18nSnapshot,
  isI18nConfigured,
  locale,
  localeDisplayName,
  registeredCatalogues,
  rememberLocale,
  rememberedLocale,
  renderI18n,
  resolveBrowserContext,
  setLocale,
  setUseCase,
  translate,
  translator,
  useCase,
} from '@crown/i18n/ui';
export { mergeCatalogueSets };

/** `data-i18n` attributes for a template literal, so the binder keeps the text current. */
export function i18nAttr(
  key: UiMessageKey | (string & {}),
  args?: MessageArgs,
): string {
  const params = args
    ? ` data-i18n-params='${JSON.stringify(args).replaceAll("'", '&#39;')}'`
    : '';
  return `data-i18n="${key}"${params}`;
}

/** Text plus its `data-i18n` marker, for element construction outside a template. */
export function localise(
  element: Element,
  key: UiMessageKey | (string & {}),
  args?: MessageArgs,
): void {
  element.setAttribute('data-i18n', key);
  if (args) element.setAttribute('data-i18n-params', JSON.stringify(args));
  element.textContent = translate(key, args);
}

export interface StartI18nOptions {
  /** The app's own catalogues, merged over the shared ones. */
  readonly catalogues?: CatalogueSet | undefined;
  /** What is already known about the caller; usually nothing before sign-in. */
  readonly settings?: I18nSettings | undefined;
  readonly timeZone?: string | undefined;
}

let started: CatalogueSet | undefined;

/**
 * One call at app boot: configures the translator from the browser's
 * languages and whatever settings are known, sets `<html lang dir>` and
 * binds `data-i18n` markup on the page. Returns the merged catalogues so a
 * later `adoptI18nSettings` resolves against the same set.
 */
export function startI18n(options: StartI18nOptions = {}): CatalogueSet {
  // An app registers its own catalogue with `addCatalogues` at import time,
  // before it starts; `configureI18n` replaces the set, so keep what is there.
  const catalogues = mergeCatalogueSets(
    uiCatalogues,
    registeredCatalogues(),
    options.catalogues ?? {},
  );
  started = catalogues;
  configureI18n({
    catalogues,
    context: resolveBrowserContext(options.settings ?? {}, catalogues),
    timeZone: options.timeZone,
  });
  applyDocumentLocale();
  if (hasGlobal('document')) bindI18n(document.body);
  return catalogues;
}

/**
 * Re-resolves once the session, organisation or workspace is known: the
 * person's own locale, the workspace's use case, the organisation's country.
 */
export function adoptI18nSettings(settings: I18nSettings): void {
  const context = resolveBrowserContext(settings, started ?? uiCatalogues);
  setLocale(context.locale);
  setUseCase(context.useCase);
  applyDocumentLocale();
}

/** The auth client members the session adoption reads; every one optional so any app's client fits. */
export interface I18nSessionClient {
  readonly workspace?: {
    readonly get?: (input: {
      workspaceId: string;
    }) => Promise<{ data?: unknown }>;
  };
  readonly organization?: {
    readonly getFullOrganization?: (input: {
      query: { organizationId: string };
    }) => Promise<{ data?: unknown }>;
  };
}

export interface I18nSession {
  /** The session's user; clients that never declared `locale` still fit. */
  readonly user?: object | null | undefined;
  readonly session?:
    | {
        readonly activeOrganizationId?: string | null | undefined;
        readonly activeWorkspaceId?: string | null | undefined;
      }
    | null
    | undefined;
}

/** The three wording fields a workspace or organisation record may carry. */
interface WordingRecord {
  readonly locale?: unknown;
  readonly defaultLocale?: unknown;
  readonly useCase?: unknown;
  readonly countryCode?: unknown;
}

function asWordingRecord<T>(value: T): WordingRecord | undefined {
  return isObjectLike(value) ? value : undefined;
}

function asTag<T>(value: T): string | undefined {
  return isString(value) && value !== '' ? value : undefined;
}

/**
 * Reads the wording settings behind a session: the person's locale from the
 * session, the workspace overrides and the organisation defaults from their
 * records. A fetch that fails leaves that layer empty rather than the page
 * without text.
 */
export async function loadI18nSettings(
  client: I18nSessionClient,
  session: I18nSession | null | undefined,
): Promise<I18nSettings> {
  const workspaceId = session?.session?.activeWorkspaceId;
  const organizationId = session?.session?.activeOrganizationId;
  const [workspace, organization] = await Promise.all([
    workspaceId && client.workspace?.get
      ? client.workspace.get({ workspaceId }).then(
          (r) => r.data,
          () => undefined,
        )
      : undefined,
    organizationId && client.organization?.getFullOrganization
      ? client.organization
          .getFullOrganization({ query: { organizationId } })
          .then(
            (r) => r.data,
            () => undefined,
          )
      : undefined,
  ]);
  const ws = asWordingRecord(workspace);
  const org = asWordingRecord(organization);
  const user = asWordingRecord(session?.user);
  return {
    user: user && { locale: asTag(user.locale) },
    workspace: ws && {
      locale: asTag(ws.locale),
      useCase: asTag(ws.useCase),
      countryCode: asTag(ws.countryCode),
    },
    organization: org && {
      defaultLocale: asTag(org.defaultLocale),
      useCase: asTag(org.useCase),
      countryCode: asTag(org.countryCode),
    },
  };
}

/** `loadI18nSettings` then `adoptI18nSettings`: the one call an app makes once signed in. */
export async function adoptSessionI18n(
  client: I18nSessionClient,
  session: I18nSession | null | undefined,
): Promise<void> {
  adoptI18nSettings(await loadI18nSettings(client, session));
}

/**
 * The auth client member `switchLocale` writes through. `$fetch` is what every
 * Better Auth client exposes, and it carries the session cookie; the typed
 * `updateUser` would need each app's client to declare the `locale` field.
 */
export interface LocaleUpdateClient {
  /** Whatever it resolves to is not read: the write either lands or throws. */
  readonly $fetch: (
    path: string,
    init: { method: 'POST'; body: Record<string, string> },
  ) => Promise<object | void>;
}

export interface SwitchLocaleOptions {
  readonly client?: LocaleUpdateClient | undefined;
  /** Reload after switching (the default) so text assembled at module scope re-evaluates too. */
  readonly reload?: boolean | undefined;
}

/**
 * The person picks a language: it is remembered in this browser, written to
 * their account when one is signed in, applied to the live translator, and
 * the page reloads so every string, including copy built once at module
 * scope, comes back in the new language.
 */
export async function switchLocale(
  next: string,
  options: SwitchLocaleOptions = {},
): Promise<void> {
  rememberLocale(next);
  const context = resolveBrowserContext(
    { user: { locale: next } },
    registeredCatalogues(),
  );
  await ensureLocaleCatalogues(context.locale);
  setLocale(context.locale);
  applyDocumentLocale();
  if (options.client) {
    try {
      await options.client.$fetch('/update-user', {
        method: 'POST',
        body: { locale: next },
      });
    } catch (error) {
      console.warn(
        '[i18n] the locale was applied here but not saved to the account',
        error,
      );
    }
  }
  if (options.reload !== false && hasGlobal('location')) location.reload();
}

export function availableUiLocales(): string[] {
  return availableLocales(registeredCatalogues());
}
